import { Typography, FormControl, Stack } from '@mui/material'
import { RequiredChip } from '~/components/common/RequiredChip'
import { SendBackReasonItem } from '../campaigns/CampaignEdit/SendBackReasonItem'
import { ReviewSection, ReviewTarget, FormatVersion } from '~/generated/graphql'
import { useCampaignInputContext } from './CampaignInputContext'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { colorPalette } from '~/constants/styles/colors'
import { LightTooltip } from '~/components/common/LightTooltip'

type Props = {
  title: string
  required?: boolean
  description?: React.ReactNode | string
  tooltip?: React.ReactNode
  children: React.ReactNode
  review?: {
    section: ReviewSection
    targets: ReviewTarget[]
  }
  compatibleFormatVersions?: FormatVersion[]
}

export type CampaignInputComponentProps<T = Record<string, unknown>> = T & {
  description?: Props['description']
  tooltip?: Props['tooltip']
}

export const CampaignInputComponent = ({
  required = false,
  description,
  title,
  tooltip,
  review,
  children,
  compatibleFormatVersions,
}: Props) => {
  const context = useCampaignInputContext()

  if (context && compatibleFormatVersions && !compatibleFormatVersions.includes(context.formatVersion)) {
    return null
  }

  return (
    <FormControl fullWidth>
      <Stack gap={1}>
        <Stack direction="row" gap={0.5} alignItems="center">
          <Typography variant="headingM">{title}</Typography>
          {required && <RequiredChip />}
          {tooltip && (
            <LightTooltip title={tooltip} placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 'inherit', color: colorPalette.other.backdrop }} />
            </LightTooltip>
          )}
        </Stack>

        {description && <Typography variant="bodyM">{description}</Typography>}
        {review && <SendBackReasonItem reviewSection={review.section} reviewTarget={review.targets} />}
        {children}
      </Stack>
    </FormControl>
  )
}
